import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PlainI18nProps } from "shared/types/i18n";
import { v4 as uuidv4 } from "uuid";

import {
  LoadingContextType,
  LoadingScreenRequest,
  LoadingScreenRequests,
} from "./types/loading-screen-provider-types";
import { LoadingIndicator } from "~/components/loading-spinner";
import { I18nProps } from "~/components/text";
import { cn } from "~/util/cn";

export const LoadingScreenProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [loadingQueue, setLoadingQueue] = useState<LoadingScreenRequests>([]);

  const handleLoadingScreenRequest = useCallback(
    (item: LoadingScreenRequest) => {
      setLoadingQueue((prevItems) => {
        if (!item.isLoading) {
          return prevItems.filter((i) => i.id !== item.id);
        }

        const existingIndex = prevItems.findIndex((i) => i.id === item.id);

        if (existingIndex !== -1) {
          const updatedItems = [...prevItems];
          updatedItems[existingIndex] = item;
          return updatedItems;
        }

        return [...prevItems, item];
      });
    },
    [],
  );

  const isLoading = useMemo(
    () => loadingQueue.some((item) => item.isLoading),
    [loadingQueue],
  );

  const Component = useMemo(() => loadingQueue[0]?.component, [loadingQueue]);

  return (
    <LoadingContext.Provider
      value={{
        loadingScreenRequests: loadingQueue,
        handleLoadingScreenRequest,
      }}
    >
      {isLoading &&
        (Component ? (
          <Component />
        ) : (
          <LoadingScreen message={loadingQueue[0]?.message} />
        ))}
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoadingScreen = ({
  isLoading,
  message,
  component,
  debounceBy = 300,
}: {
  isLoading: boolean;
  message?: I18nProps;
  component?: React.FC;
  debounceBy?: number;
}): void => {
  const { handleLoadingScreenRequest } = useLoadingContext();
  const id = useMemo(() => uuidv4(), []);

  useEffect(() => {
    const debounceLoadingScreenTimeout = setTimeout(() => {
      handleLoadingScreenRequest({
        id,
        isLoading,
        message,
        component,
      });
    }, debounceBy);

    return () => {
      clearTimeout(debounceLoadingScreenTimeout);
    };
  }, [
    component,
    debounceBy,
    handleLoadingScreenRequest,
    id,
    isLoading,
    message,
  ]);
};

const useLoadingContext = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoadingContext must be used within a LoadingProvider");
  }
  return context;
};

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

const LoadingScreen: React.FC<{
  message?: PlainI18nProps;
  className?: string;
}> = ({ message = { tx: "general.loading" }, className }) => {
  return (
    <div
      className={cn(
        "min-h-dvh fixed z-50 flex h-full w-full flex-col items-center justify-center bg-white",
        className,
      )}
    >
      <LoadingIndicator as="spinner" className={className} message={message} />
    </div>
  );
};
